// @ts-nocheck

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileUpload } from '@/components/ui/file-upload';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { getUser } from '@/utilities/setting';
import { AI_NAME_MAX_LENGTH } from '@/utilities/aiAgentsStore';
import {
  ROUTES,
  KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES,
  KNOWLEDGE_BASE_TYPE_OPTIONS,
  KNOWLEDGE_BASE_ACCEPT,
} from '@/utilities/constant';
import { createKnowledgeBase, checkUrlReachable, listKnowledgeBases } from '@/service/knowledgeBaseApi';
import { validateKnowledgeBaseFile } from '@/utilities/knowledgeBaseFile';

const SOURCE_TYPES = [
  { value: 'file', label: 'Upload file', hint: 'PDF, DOC, DOCX and TXT documents' },
  { value: 'url', label: 'Website / URL', hint: 'Crawl and ingest a public URL' },
];

const MAX_FILE_LABEL = `${(KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MB`;


const URL_REGEX =
  /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::\d{1,5})?(?:[/?#]\S*)?$/;

const DUPLICATE_NAME_MESSAGE =
  'A knowledge base with this name already exists. Please choose a different name.';


const rowName = (row: any) => String(row?.name ?? row?.kb_name ?? '').trim().toLowerCase();

const CreateKnowledgeBasePage = () => {
  const navigate = useNavigate();
  const user = getUser();

  const [kbName, setKbName] = useState('');
  const [kbType, setKbType] = useState(KNOWLEDGE_BASE_TYPE_OPTIONS[0]?.value || 'general');
  const [sourceType, setSourceType] = useState('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const backTo = `/${ROUTES.AI_AGENT.KNOWLEDGE_BASE_LIST}`;

  const validate = () => {
    const next: any = {};
    if (!kbName.trim()) next.kbName = 'Knowledge base name is required';
    else if (kbName.trim().length > AI_NAME_MAX_LENGTH)
      next.kbName = `Name must be ${AI_NAME_MAX_LENGTH} characters or fewer`;
    if (sourceType === 'url') {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) next.url = 'Enter a URL to ingest';
      else if (!URL_REGEX.test(trimmedUrl)) next.url = 'Enter a valid URL, e.g. https://example.com';
    }
    if (sourceType === 'file' && file.length === 0) next.file = 'Choose a file to upload';
    setErrors(next);
    return Object.keys(next).length === 0;
  };


  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      setFile([]);
      return;
    }
    const fileError = await validateKnowledgeBaseFile(files[0]);
    if (fileError) {
      setFile([]);
      setErrors((prev: any) => ({ ...prev, file: fileError }));
      toast.error(fileError);
      return;
    }
    setFile(files);
    setErrors((prev: any) => ({ ...prev, file: undefined }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validate()) return;

    if (sourceType === 'file') {
      const fileError = await validateKnowledgeBaseFile(file[0]);
      if (fileError) {
        setErrors((prev: any) => ({ ...prev, file: fileError }));
        toast.error(fileError);
        return;
      }
    }

 
    setCheckingUrl(true); 
    try {
      const existing = await listKnowledgeBases();
      const wanted = kbName.trim().toLowerCase();
      if (existing.some((row: any) => rowName(row) === wanted)) {
        setErrors((prev: any) => ({ ...prev, kbName: DUPLICATE_NAME_MESSAGE }));
        toast.error(DUPLICATE_NAME_MESSAGE);
        setCheckingUrl(false);
        return;
      }
    } catch {
    
    }
    setCheckingUrl(false);

    if (sourceType === 'url') {
      setCheckingUrl(true);
      try {
        await checkUrlReachable(url.trim());
      } catch (err: any) {
        setErrors((prev: any) => ({
          ...prev,
          url: typeof err === 'string' ? err : 'Wrong URL entered — this URL could not be reached.',
        }));
        toast.error(typeof err === 'string' ? err : 'Wrong URL entered — this URL could not be reached.');
        setCheckingUrl(false);
        return;
      }
      setCheckingUrl(false);
    }

    setSaving(true);
    try {
      await createKnowledgeBase({
        kbName: kbName.trim(),
        userId: user?.id,
        kbType: kbType || undefined,
        resellerId: user?.company_id ? String(user.company_id) : undefined,
        url: sourceType === 'url' ? url.trim() : undefined,
        file: sourceType === 'file' ? file[0] : undefined,
      });
      toast.success('Knowledge base creation started');
      navigate(backTo);
    } catch (err: any) {
      const message = typeof err === 'string' ? err : 'Failed to create knowledge base';

      if (/already (taken|exists)/i.test(message)) {
        setErrors((prev: any) => ({ ...prev, kbName: message }));
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollPage>
      <PageHeader
        title="New Knowledge Base"
        description="Create a knowledge base for the AI Agent from a document or a website URL."
        actions={
          <Button asChild variant="outline">
            <Link to={backTo}>
              <ArrowLeft className="h-4 w-4" />
              Back to Knowledge Base
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
            <CardDescription>Name the knowledge base and pick its type.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="kb-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="kb-name"
                placeholder="e.g. Support FAQ"
                value={kbName}
                onChange={(e) => {
                  setKbName(e.target.value);
                  if (errors.kbName) setErrors((prev: any) => ({ ...prev, kbName: undefined }));
                }}
                maxLength={AI_NAME_MAX_LENGTH}
                aria-invalid={!!errors.kbName}
                className={errors.kbName ? 'border-destructive focus-visible:ring-destructive' : undefined}
              />
              <div className="flex items-start justify-between gap-2">
                {errors.kbName ? (
                  <p className="text-xs text-destructive">{errors.kbName}</p>
                ) : (
                  <span />
                )}
                <p className="shrink-0 text-xs text-muted-foreground">
                  {kbName.length}/{AI_NAME_MAX_LENGTH}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-type">Type</Label>
              <Select value={kbType} onValueChange={setKbType}>
                <SelectTrigger id="kb-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {KNOWLEDGE_BASE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Source</CardTitle>
            <CardDescription>Feed the knowledge base from one file or one URL.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup
              value={sourceType}
              onValueChange={(v) => {
                setSourceType(v);
                setErrors((e: any) => ({ ...e, url: undefined, file: undefined }));
              }}
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              {SOURCE_TYPES.map((opt) => {
                const selected = sourceType === opt.value;
                return (
                  <label
                    key={opt.value}
                    htmlFor={`kb-source-${opt.value}`}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <RadioGroupItem value={opt.value} id={`kb-source-${opt.value}`} className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.hint}</div>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>

            {sourceType === 'url' ? (
              <div className="space-y-2">
                <Label htmlFor="kb-url">
                  URL <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="kb-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://example.com/help"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>
                  File <span className="text-destructive">*</span>
                </Label>
                <FileUpload
                  value={file}
                  onChange={handleFileChange}
                  accept={KNOWLEDGE_BASE_ACCEPT}
                  hint={`PDF, DOC, DOCX or TXT — up to ${MAX_FILE_LABEL}`}
                  maxSize={KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES}
                />
                {errors.file && <p className="text-xs text-destructive">{errors.file}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(backTo)} disabled={saving || checkingUrl}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || checkingUrl}>
            {saving || checkingUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {checkingUrl ? 'Checking…' : 'Create'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default CreateKnowledgeBasePage;
